/* The row still carries `data-assigned` for anyone who needs to target it, but an unfilled
   position is marked by its badge rather than by dimming its text — typography comes from the
   shared table, not from this stylesheet. */

/* `GroupHeader` brings its own background (`--color-surface-secondary`) and its own
   `0.8rem 1.6rem` padding — zero this cell's out so the two don't stack. */
.group {
  padding: 0;

  &:first-child {
    padding-left: 0;
  }
}

.badge {
  margin-left: 0.8rem;
}

/* The interactive wrapper around the "Unassigned" chip, once it becomes a control. Reset to a
   bare inline element so the chip's own visuals carry the look — hover brightens the chip's own
   background (one step up the grayscale scale from its resting `--color-grayscale-arsenic`,
   the same step `--color-surfaces-bg-elevation-4` uses) rather than drawing an outline around it,
   since an outline traces this button's box, not the chip's pill, and the two shapes don't match.
   Focus keeps a visible ring — the chip is a real control now and must stay keyboard-discoverable
   — sized to the chip's own radius so it doesn't reproduce that mismatch. */
.assignButton {
  display: inline-flex;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:hover .badge {
    background: var(--color-grayscale-trout);
  }

  &:focus-visible {
    outline: 1px solid var(--color-grayscale-ghost);
    outline-offset: 1px;
    border-radius: var(--border-radius-medium);
  }
}

/* Narrow, right-aligned trailing column: the menu trigger should not steal width from the data. */
.actions {
  width: 1%;
  text-align: right;
  white-space: nowrap;
}

/* The name cell holds two things at once while inline editing is on — a full-width input and,
   for an unfilled position, the "Unassigned" badge. The shared table sets `input { width: 100% }`
   on every cell, so without this the badge is pushed onto a second line. Only applied when the
   cell is actually editable; a read-only cell keeps the plain text flow it always had. */
.nameCell[data-editable='true'] {
  display: flex;
  align-items: center;

  /* The `InlineEditableText` container. Allowed to take the slack, allowed to shrink — a long
     name must ellipsise rather than shove the badge out of the cell. */
  > *:first-child {
    flex: 1;
    min-width: 0;
  }

  .badge {
    flex-shrink: 0;
  }
}

/* The role/department/team triggers, restyled to sit in a row next to `InlineEditableText`.
   The kit's own trigger carries a permanent border — right in a form, but three boxed cells per
   row against three borderless text cells reads as two different tables. So the background and
   border appear on hover, focus and while open, exactly as the editable text cells do it. The
   negative inline-start margin is the same optical trick `inline-editable-text.module.scss`
   uses, so a picker's text lines up with the column header above it. */
.picker {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 3.2rem;
  margin-inline-start: -1.2rem;
  padding: 0 1.2rem;
  border: 1px solid transparent;
  border-radius: var(--border-radius-medium);
  background: transparent;
  color: var(--color-grayscale-ghost);
  font-size: 1.4rem;
  font-weight: 400;
  text-align: start;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover,
  &[data-state='open'] {
    background-color: var(--color-grayscale-arsenic);
    color: var(--color-grayscale-white);
  }

  &:focus-visible {
    border-color: var(--color-secondary);
    outline: none;
  }
}

/* An empty picker names its own column rather than showing a dash — that is the kit's own
   empty-state behaviour (`styled-empty` renders `title`), and it is what gives the trigger an
   accessible name when there is no value to read out. */
.pickerEmpty {
  color: var(--color-grayscale-light-slate);
}
